import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { InfoPopover } from "./InfoPopover";
import { type EvidenceGraphView } from "../../../domains/gpt/hooks/useEvidenceGptChat";

type JuisciGrade = NonNullable<EvidenceGraphView["juisciGrade"]>;

// The JuisciGrade scale (A/B/C/D/U — see docs/juisci-gpt-evidence-graph.md §9):
// the strength of the best available evidence behind the answer. `U` is
// "ungraded" (no gradeable claim survived the fidelity gate), not a low grade.
// Exported so the Evidence details panel's per-claim chips share the palette.
export const GRADE_STYLE = {
  A: { dot: "bg-emerald-500", ring: "border-emerald-200 bg-emerald-50" },
  B: { dot: "bg-lime-500", ring: "border-lime-200 bg-lime-50" },
  C: { dot: "bg-amber-500", ring: "border-amber-200 bg-amber-50" },
  D: { dot: "bg-orange-500", ring: "border-orange-200 bg-orange-50" },
  U: { dot: "bg-ds-slate-400", ring: "border-ds-slate-200 bg-ds-slate-50" },
} as const satisfies Record<JuisciGrade, { dot: string; ring: string }>;

// Exported for the Evidence tab's per-claim confidence popover, so the grade
// wording has one definition.
export function gradeLabel(
  grade: JuisciGrade,
  t: (key: string) => string
): string {
  switch (grade) {
    case "A":
      return t("High-certainty evidence");
    case "B":
      return t("Moderate-certainty evidence");
    case "C":
      return t("Low-certainty evidence");
    case "D":
      return t("Very-low-certainty evidence");
    case "U":
      return t("Unscored");
    default:
      throw new Error(grade satisfies never);
  }
}

export function JuisciGradeBadge({
  grade,
  className,
}: {
  grade: JuisciGrade;
  className?: string;
}) {
  const { t } = useTranslation();
  const style = GRADE_STYLE[grade];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1",
        "text-xs font-semibold text-ds-black",
        style.ring,
        className
      )}
    >
      <span className={cn("size-2 rounded-full", style.dot)} />
      {t("Confidence score")} {grade}
      <InfoPopover label={t("About this score")} align="left">
        <span className="block font-semibold text-white">
          {t("Juisci confidence score")}
        </span>
        <span className="block">
          {t("The strength of the best available evidence behind this answer.")}
        </span>
        <span className="mt-1 block">
          {t("Confidence score")} {grade}: {gradeLabel(grade, t)}
        </span>
      </InfoPopover>
    </span>
  );
}
